import { create } from "zustand";
import type { ManifestLayer } from "../types/contract";
import {
  initUiState,
  openUiState,
  BOUNDARY_LAYER_ID,
  type LayerUiState,
} from "../lib/layerRegistry";

// Save-only design reveal calls setVisible before this map has initialized the
// id, which leaves a partial entry (visible, no opacity or order). A layer
// still on from the previous visit already has both, so reopening does not
// treat it as a new reveal.
const DESIGN_REVEAL_ID = /^target-(?:points|zones)-/;

export function pendingDesignRevealIds(
  byId: Record<
    string,
    { visible?: boolean; opacity?: number; order?: number } | undefined
  >,
): string[] {
  const ids: string[] = [];
  for (const [id, st] of Object.entries(byId)) {
    if (!st?.visible || !DESIGN_REVEAL_ID.test(id)) continue;
    if (st.opacity == null || st.order == null) ids.push(id);
  }
  return ids;
}

// The field boundary (BE-light emits it with this id, §1H) is the always-on base outline — exempt
// from the one-layer-at-a-time rule so it stays under whichever data layer is shown.
const BOUNDARY_ID = BOUNDARY_LAYER_ID;

// Per-field layer UI state (visibility / opacity / order) — client-only, drives the
// configurable multi-layer map (Front-End.md §0). Keyed by layer id within the active field.
export interface LayerState {
  byId: Record<string, LayerUiState>;
  init: (layers: ManifestLayer[]) => void;
  resetForOpen: (layers: ManifestLayer[]) => void;
  reseed: (layers: ManifestLayer[]) => void;
  setVisible: (id: string, visible: boolean) => void;
  clearVisible: () => void;
  setOpacity: (id: string, opacity: number) => void;
  setOrder: (id: string, order: number) => void;
}

export const useLayerStore = create<LayerState>((set) => ({
  byId: {},
  // Merge, don't reset: preserve visibility/opacity for layers that persist across a re-init (e.g.
  // when a "view covariate on map" adds a layer to the merged set), while.
  // A partial entry (setVisible before this id was initialized) is filled from the defaults, so a
  // mid-visit Save-only reveal is not read as a new reveal on the next open.
  init: (layers) =>
    set((s) => {
      const fresh = initUiState(layers);
      const byId: Record<string, LayerUiState> = {};
      for (const l of layers) {
        const cur = s.byId[l.id];
        byId[l.id] = cur ? { ...fresh[l.id], ...cur } : fresh[l.id];
      }
      return { byId };
    }),
  // Replace, don't merge. Layer ids repeat across fields (`zones`, `ml-*`), and
  // this store lives for the whole page, so a merge would bring the last field's
  // checks back on the next open. Opacity and order come from the layer defaults.
  resetForOpen: (layers) => set({ byId: openUiState(layers) }),
  // FORCE these layers back to their manifest defaults, overriding any stale UI state.
  // Used when a client-computed set is (re)activated under REUSED ids (the fert rec layers
  // always use fixed ids fert-lime/…): the merge in init would otherwise keep the prior
  // set's visibility, hiding the freshly-selected nutrient or leaving a stale combine view.
  reseed: (layers) =>
    set((s) => {
      const fresh = initUiState(layers);
      const byId = { ...s.byId };
      for (const l of layers) byId[l.id] = fresh[l.id];
      return { byId };
    }),
  // The BOUNDARY is the always-available base outline: it's exempt both ways (toggling it never
  // solos, and it's never hidden by soloing another layer).
  setVisible: (id, visible) =>
    set((s) => {
      if (!visible || id === BOUNDARY_ID) {
        return { byId: { ...s.byId, [id]: { ...s.byId[id], visible } } };
      }
      const byId = { ...s.byId };
      for (const k of Object.keys(byId)) {
        // sample-kriging underlays are FOLLOWERS: never toggled directly (no sidebar row), composed
        // into the map only while their source sample layer is visible. Solo-hiding their ui entry
        // here would strand it false — the source re-checks, the underlay is composed again, but
        // the stale entry keeps the surface unpainted under the dots.
        if (
          k !== id &&
          k !== BOUNDARY_ID &&
          !k.startsWith("sample-krige-") &&
          byId[k].visible
        )
          byId[k] = { ...byId[k], visible: false };
      }
      byId[id] = { ...byId[id], visible: true };
      return { byId };
    }),
  // Deselect EVERY non-boundary layer (the boundary is the always-on base outline).
  clearVisible: () =>
    set((s) => {
      const byId = { ...s.byId };
      for (const k of Object.keys(byId)) {
        if (k !== BOUNDARY_ID && byId[k].visible)
          byId[k] = { ...byId[k], visible: false };
      }
      return { byId };
    }),
  setOpacity: (id, opacity) =>
    set((s) => ({ byId: { ...s.byId, [id]: { ...s.byId[id], opacity } } })),
  setOrder: (id, order) =>
    set((s) => ({ byId: { ...s.byId, [id]: { ...s.byId[id], order } } })),
}));
